import { useEffect, useRef, useState, type Dispatch, type RefObject, type SetStateAction } from "react";
import { receiptImageGroups, type ReceiptAttachment } from "../../lib/image-data";
import type { MooMessage } from "../../types/moo";
import { sendMoo } from "./moo-client";

type PendingSend = { chatId: string; requestId: string; text: string; photos: ReceiptAttachment[]; context: string };

export function useMooSend({ chatId, context, busy, setBusy, setError, setMessages, inFlight, onChanged, onSent }: {
  chatId: string; context: string; busy: string;
  setBusy: (value: string) => void; setError: (value: string) => void;
  setMessages: Dispatch<SetStateAction<MooMessage[]>>;
  inFlight: RefObject<boolean>; onChanged: () => void; onSent: () => void;
}) {
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState<ReceiptAttachment[]>([]);
  const [pending, setPending] = useState<PendingSend | null>(null);
  const [previews, setPreviews] = useState<Record<string, ReceiptAttachment[]>>({});
  const pendingSend = useRef(false);
  const prepared = useRef<{ requestId: string; groups: Awaited<ReturnType<typeof receiptImageGroups>> } | null>(null);
  useEffect(() => { setPending(null); pendingSend.current = false; setText(""); setPhotos([]); setPreviews({}); prepared.current = null; }, [chatId]);

  async function deliver(draft: PendingSend) {
    inFlight.current = true;
    setError("");
    setBusy(draft.photos.length ? "Reading receipts…" : "Moo is thinking…");
    try {
      if (prepared.current?.requestId !== draft.requestId)
        prepared.current = { requestId: draft.requestId, groups: await receiptImageGroups(draft.photos) };
      const { imageGroups, thumbnailGroups } = prepared.current.groups;
      const result = await sendMoo({ chatId: draft.chatId, requestId: draft.requestId, text: draft.text,
        imageGroups: imageGroups.length ? imageGroups : undefined,
        thumbnailGroups: imageGroups.length ? thumbnailGroups : undefined, context: draft.context }, setBusy);
      const received = result.messages.some(message => message.id === draft.requestId);
      if (received) setMessages(result.messages);
      if (!received || result.messages.at(-1)?.role !== "assistant")
        throw new Error("Your message reached Moo, but its reply is incomplete. Reopen this conversation to check progress.");
      if (result.messages.at(-1)?.payload.status === "saved") onChanged();
      setPending(null);
      pendingSend.current = false;
      setPreviews(current => {
        const next = { ...current };
        if (result.messages.find(message => message.id === draft.requestId)?.payload.attachments?.length) delete next[draft.requestId];
        return next;
      });
      prepared.current = null;
      onSent();
    } catch (error) {
      setError(`${error instanceof Error ? error.message : "The connection was interrupted."} Your message is kept in this conversation.`);
    } finally {
      setBusy("");
      inFlight.current = false;
    }
  }
  function send() {
    if (inFlight.current || busy || (!text.trim() && !photos.length)) return;
    const draft = { chatId, requestId: crypto.randomUUID(), text: text.trim(), photos, context };
    setPending(draft);
    pendingSend.current = true;
    setPreviews(current => ({ ...current, [draft.requestId]: photos }));
    setMessages(current => [...current, { id: draft.requestId, role: "user", content: draft.text || "Read this receipt", payload: {}, created_at: Date.now() }]);
    setText(""); setPhotos([]);
    void deliver(draft);
  }
  function retry() {
    if (pending && !busy && !inFlight.current) void deliver(pending);
  }
  return { text, setText, photos, setPhotos, previews, pendingSend, send, retry: pending && !busy ? retry : undefined };
}
